import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useUserEnvStore } from '@/common/stores/user-env';
import { checkRole } from '@/common/auth/auth.utils';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useDashboardJumpStore } from '@/dashboard/stores/dashboard-jump';

export interface DashboardList {
  dashboardId: number;
  dashboardName: string;
  url?: string;
  isNewWindow?: boolean;
}

export interface UrlInfo {
  checked: boolean;
  url: string;
  urlName: string;
  index: number;
  isGlobal: boolean;
  id: string;
  isNewWindow?: boolean;
}

export interface UrlInfoForUi extends Omit<UrlInfo, 'index' | 'isGlobal' | 'id'> {
  id?: string;
  isGlobal?: boolean;
  urlErrorMsg?: string;
  nameErrorMsg?: string;
}

export interface DashboardJumpData {
  name: string;
  dashboardList: any[];
  urlList: any[];
  checked: boolean;
}

export const useDashboardPopupEnvStore = defineStore('dashboardPopupEnvStore', () => {
  // setupDashBoardPopup.ts 에서 onMounted 훅에서 보장한다는 가정
  const dashboardJumpStore = useDashboardJumpStore();
  const { myInfo } = storeToRefs(useMyInfoStore());
  const { dashboardJumpList } = storeToRefs(dashboardJumpStore);
  const dashboardListMap = computed(() => {
    return new Map(dashboardJumpList.value.map((v) => [v.dashboardId, v]));
  });

  const userEnvStore = useUserEnvStore();
  const isMaster = computed(() => {
    const myRole = myInfo.value.role;
    return checkRole.isMaster(myRole);
  });

  const setupDashBoardData = ref<Map<string, DashboardJumpData>>(new Map());

  const deleteSetupDashboardDataByEnvKey = (envKey: string) => {
    setupDashBoardData.value.delete(envKey);
  };

  const updateSetupDashboardDataByEnvKey = (envKey: string, data: DashboardJumpData) => {
    setupDashBoardData.value.set(envKey, data);
  };

  const mergeObjects = (globalObj: UrlInfo[], envObj: UrlInfo[]) => {
    const mergedList = [...globalObj];

    envObj.forEach((item2) => {
      if (item2.isGlobal) {
        const foundIndex = mergedList.findIndex((item1) => item1.isGlobal && item1.id === item2.id);

        if (foundIndex !== -1) {
          mergedList[foundIndex] = {
            ...mergedList[foundIndex],
            checked: item2.checked,
          };
        }
      } else {
        mergedList.push(item2);
      }
    });

    return mergedList;
  };

  const getFilteredEnvData = (envDashboardList: DashboardJumpData['dashboardList']) => {
    return (
      envDashboardList
        ?.filter((v) => dashboardListMap.value.get(v.dashboardId))
        .map((v) => ({
          ...v,
          dashboardName:
            dashboardListMap.value.get(v.dashboardId)?.dashboardName || v.dashboardName,
        })) ?? []
    );
  };
  const fetchDashboardPopupEnvInfoForKey = async (envKey?: string) => {
    if (!envKey) return;
    await userEnvStore.fetchEnvDataByKeys([`${envKey}_global`], true);
    const globalEnvData = userEnvStore.getEnvValue<DashboardJumpData>(`${envKey}_global`);
    const envData = userEnvStore.getEnvValue<DashboardJumpData>(envKey);

    if ((isMaster.value && globalEnvData) || (!envData && globalEnvData)) {
      const filteredDashboardList = getFilteredEnvData(globalEnvData?.dashboardList ?? []);

      if (globalEnvData.urlList?.length > 0) {
        globalEnvData.urlList.forEach((v) => {
          if (v.checked) {
            filteredDashboardList.push({
              dashboardId: null,
              dashboardName: v.urlName,
              url: v.url,
              isNewWindow: v.isNewWindow,
            });
          }
        });
      }
      setupDashBoardData.value.set(envKey, {
        ...globalEnvData,
        dashboardList: filteredDashboardList,
        urlList:
          globalEnvData.urlList?.map((v) => ({
            ...v,
            isNewWindow: v.isNewWindow ?? false,
          })) || [],
      });
    } else if (envData) {
      const filteredUrlList =
        globalEnvData?.urlList?.length || envData.urlList?.length > 0
          ? mergeObjects(globalEnvData?.urlList || [], envData.urlList)
          : [];
      const filteredDashboardList = getFilteredEnvData([...envData.dashboardList]);

      if (filteredUrlList?.length > 0) {
        filteredUrlList.forEach((v) => {
          if (v.checked) {
            filteredDashboardList.push({
              dashboardId: null,
              dashboardName: v.urlName,
              url: v.url,
              isNewWindow: v.isNewWindow,
            });
          }
        });
      }

      setupDashBoardData.value.set(envKey, {
        ...envData,
        dashboardList: filteredDashboardList,
        urlList:
          filteredUrlList?.map((v) => ({
            ...v,
            isNewWindow: v.isNewWindow ?? false,
          })) || [],
      });
    }
  };

  const saveEnvInfoForKey = async (envKey: string, value: string) => {
    if (isMaster.value) {
      await userEnvStore.setEnvValue([{ key: `${envKey}_global`, value }], true);
    } else {
      await userEnvStore.setEnvValue([{ key: envKey, value }]);
    }
  };

  return {
    setupDashBoardData,
    fetchDashboardPopupEnvInfoForKey,
    saveEnvInfoForKey,
    deleteSetupDashboardDataByEnvKey,
    updateSetupDashboardDataByEnvKey,
  };
});
